import { ButtonGroup, Checkbox, Popover, SvgIcon, SxProps, Tooltip, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import React, { useMemo, useRef, useState } from 'react';
import { useStyling } from '../../Theme';
import { BOOLEAN_STRINGS_CONSTANTS } from '../../constants/constants';
import { Icons } from '../../icons/Icons';

export type QuickFilter = {
    id: string;
    label: string;
    options: string[];
    originalOptions?: string[];
    displayType?: string;
    values?: string[];
    tooltip?: string;
    icon?: typeof SvgIcon;
};

export type FilterItemProps = {
    filter: QuickFilter;
    onFilterChanged: (QuickFilter) => void;
    requiredFilters: string[];
    sx?: SxProps;
};

export default (props: FilterItemProps) => {
    const { filter, onFilterChanged, requiredFilters } = props;
    const { theme } = useStyling();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const isRequiredFilter = requiredFilters.includes(filter.id);

    const QUICK_FILTER_BOOLEAN_VALUES = {
        true: 'True',
        false: 'False'
    };

    const order = useMemo(() => {
        const map = {};
        filter.options.forEach((o, index) => {
            map[o] = index;
        });
        return map;
    }, [filter]);

    const normalizeOption = (option: string) => {
        return option.trim() === QUICK_FILTER_BOOLEAN_VALUES.true
            ? BOOLEAN_STRINGS_CONSTANTS.true
            : option.trim() === QUICK_FILTER_BOOLEAN_VALUES.false
              ? BOOLEAN_STRINGS_CONSTANTS.false
              : option;
    };

    const handleMenuItemClick = (option: string) => {
        const normalizedOption = normalizeOption(option);

        if (!filter.values) {
            filter.values = [];
        }

        if (filter.values.includes(normalizedOption)) {
            filter.values = filter.values.filter((v) => v !== normalizedOption);
        } else {
            filter.values = [...filter.values, normalizedOption];
        }

        onFilterChanged(filter);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event?: Event) => {
        if (event && anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    const handleClear = () => {
        handleClose();
        filter.values = [];
        onFilterChanged(filter);
    };

    const { hasValues, more, text } = useMemo(() => {
        const len = filter.values ? filter.values.length : 0;
        const hasValues = len > 0;
        const more = len > 1 ? '+' + (len - 1) : '';
        const text = hasValues ? filter.values.sort((a, b) => order[a] - order[b])[0] : filter.label;
        return { hasValues, more, text };
    }, [filter.values]);

    const isOptionSelected = (option: string) => {
        if (!filter.values) return false;
        const normalizedOption = normalizeOption(option);
        return filter.values.includes(normalizedOption);
    };

    return (
        <React.Fragment>
            <ButtonGroup
                color={hasValues ? 'active' : 'inactive'}
                variant={hasValues ? 'contained' : 'outlined'}
                ref={anchorRef}
                size={'small'}
            >
                <Tooltip
                    title={hasValues && !React.isValidElement(text) ? text : filter.tooltip || ''}
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, -6]
                                    }
                                }
                            ]
                        }
                    }}
                >
                    <Button
                        size={'small'}
                        sx={{ borderRadius: '5px', textWrap: 'nowrap', maxWidth: '150px' }}
                        onClick={handleToggle}
                        startIcon={filter.icon ? <filter.icon /> : null}
                    >
                        <p className="ellipsis" style={{ margin: 0 }}>
                            {text}
                        </p>
                        {more ? (
                            <Typography
                                style={{
                                    color: theme.palette.text.disabled,
                                    paddingLeft: '4px',
                                    margin: 0
                                }}
                            >
                                {more}
                            </Typography>
                        ) : (
                            <></>
                        )}
                        {hasValues && !isRequiredFilter ? (
                            <Icons.Close style={{ paddingLeft: '4px' }} onClick={handleClear} />
                        ) : (
                            <></>
                        )}
                    </Button>
                </Tooltip>
            </ButtonGroup>
            <Popover
                open={open}
                anchorEl={anchorRef.current}
                disablePortal={false}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'left'
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1.5,
                            width: 'auto'
                        }
                    }
                }}
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <MenuList
                        id={`filter-${filter.id}-menu`}
                        autoFocusItem
                        sx={{
                            maxHeight: '320px',
                            width: 'auto'
                        }}
                    >
                        {filter.options.map((option, index) => (
                            <MenuItem
                                key={index}
                                sx={{ py: 0, pl: 1, pr: 2 }}
                                onClick={() => handleMenuItemClick(option)}
                            >
                                <Checkbox checked={isOptionSelected(option)} />
                                {option}
                            </MenuItem>
                        ))}
                    </MenuList>
                </ClickAwayListener>
            </Popover>
        </React.Fragment>
    );
};
